import { Container } from '@almbrand/container';
import { FormWrapper } from '@almbrand/formwrapper';
import { Headline, HeadlineProps } from '@almbrand/headline';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { LoginButtonComponent, ModalComponent, StepComponent } from 'components';
import { Component } from 'components/Component';
import { IsPackageComponentContext } from 'context/IsPackageComponentContext';
import { PageContext } from 'context/PageContext';
import { useFormSubmit } from 'hooks';
import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import { getComponentProperties } from '../../../services/MapFieldService';
import { isPackageFields } from '../../../services/PackageService';
import { getReplacedValue } from '../../../services/PlaceholderService';
import { StepAuthenticationGate } from '../StepAuthenticationGate';
import styles from './PageTemplate.module.scss';

export interface PageTemplateProps {
	stepBlock?: StepBlock;
	activePage?: number;
}
export const PageTemplate: React.FC<PageTemplateProps> = memo(({ stepBlock, activePage }) => {
	const { title, fields, contentId: stepContentId, errorMessage, campaigns } = stepBlock || {};
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};

	const methods = useForm({ mode: 'onTouched' });
	const {
		handleSubmit,
		formState: { errors, isValid },
		register,
		resetField,
		setError,
		watch,
		trigger,
		setValue,
	} = methods;

	const { onSubmit } = useFormSubmit();

	const { isVisible } = useContext(IsPackageComponentContext);

	const displayModal = useAppSelector((state) => state.modal.displayModal);

	const isPackages = useMemo(() => {
		return isPackageFields(fields);
	}, [fields]);

	const { isMobile } = useDeviceQuery();

	const getFormProperties = (name?: string) => {
		return {
			stepTitle: getReplacedValue(title),
			hasError: name ? !!errors?.[name] : false,
			errors,
			errorMessage,
			register,
			resetField,
			setError,
			isValid,
			watch,
			trigger,
			setValue,
			ctaSubmitStep: () => {
				void handleSubmit(onSubmit)();
			},
			// insert this if field onchange should submit form
			// submitStep: () => handleSubmit(onSubmit)(),
		};
	};

	const mapComponents = () => {
		return fields.items.map((contentItem, index) => {
			const name = contentItem.content.uniqueIdentifier?.uid ?? 'NO_UNIQUE_ID_FOR_CONTENT_ITEM';

			const updatedComponentProps = getComponentProperties(
				stepContentId,
				contentItem,
				index,
				getFormProperties(name)
			);

			return updatedComponentProps ? (
				<Component
					key={contentItem.content.contentId}
					{...updatedComponentProps}
				/>
			) : undefined;
		});
	};

	const loginButtonUi = () => {
		const campaign: any = campaigns.items.find((campaign: any) => campaign.content?.contentType === 'ButtonBlock');
		if (campaign) {
			return (
				<Container
					mobileWidth={isPackages && isMobile ? '342' : 'fullWidth'}
					tabletWidth='996'
					desktopWidth='996'
					noPadding
				>
					<LoginButtonComponent
						key={campaign.content.contentId}
						onClick={() => {
							location.href = campaign.content.linkButtonSettings?.url ?? '';
						}}
					>
						{campaign.content.linkButtonSettings?.title ?? campaign.content.linkButtonSettings?.text}
					</LoginButtonComponent>
				</Container>
			);
		}
	};

	const pageTemplateRef = useRef<HTMLDivElement>(null);

	// Scroll to top when on next page when on mobile
	useEffect(() => {
		if (pageTemplateRef.current && pageTemplateRef.current.scrollTop > 0) {
			pageTemplateRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [activePage]);

	// Headline (move to CMS) If Privatsikring-theme then primaryDark otherwise primary. But also white on package steps for non-mobile view
	let headlineColor: HeadlineProps['fontColor'];
	if (isPackages) {
		headlineColor = 'white';
	} else if (theme === 'Privatsikring-theme') {
		headlineColor = 'primaryDark';
	} else {
		headlineColor = 'primary';
	}

	return (
		<div
			ref={pageTemplateRef}
			className={classNames(styles.PageTemplate, isVisible && styles['PageTemplate--full-width'])}
		>
			<StepAuthenticationGate stepBlock={stepBlock}>
				<FormProvider {...methods}>
					<FormWrapper
						onSubmit={handleSubmit(onSubmit)}
						className={styles.PageTemplate__Form}
					>
						<StepComponent
							fields={fields}
							resetField={resetField}
						>
							<fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
								{loginButtonUi()}
								{(!isPackages || (isPackages && !isMobile)) && (
									<Container
										mobileWidth={isPackages && !isMobile ? '342' : 'fullWidth'}
										tabletWidth={isPackages && !isMobile ? '996' : 'fullWidth'}
										desktopWidth={isPackages && !isMobile ? '996' : 'fullWidth'}
										noPadding={isPackages && !isMobile}
									>
										<legend className={styles.PageTemplate__Form__Headline}>
											<Headline
												title={getReplacedValue(title)}
												size='default'
												variant='h3'
												fontColor={headlineColor}
											/>
										</legend>
									</Container>
								)}
								<div
									className={classNames(
										styles.PageTemplate__Form__Content
										//TODO: Remove comment if business wants center style again on package step
										//isVisible && styles['PageTemplate__Form__Content--package']
									)}
								>
									{mapComponents()}
								</div>
							</fieldset>
						</StepComponent>

						{displayModal && <ModalComponent />}
					</FormWrapper>
				</FormProvider>
			</StepAuthenticationGate>
		</div>
	);
});

PageTemplate.displayName = 'PageTemplate';
